// ============================================================================
// service_state on a route row.
//
// WHY THIS IS A CLIENT-SIDE JOIN
// ------------------------------
// None of the four views the route module reads expose `service_state`:
// `v_am_routes_today`, `v_am_routes_week`, `v_am_route_template` and
// `active_facilities` all predate the pause model, and `active_facilities` is
// under a standing instruction not to be changed. So the badge rides a
// flat-fetch + IN-lookup + client-join — the house data pattern — and the
// route views stay exactly as they are.
//
// That is deliberate rather than merely convenient. Ben's ruling on the route
// leg was that a paused facility **stays on the route with a badge**: it is
// never filtered off a screen. Adding a predicate to a route view would have
// made the site vanish, which is the failure this whole model exists to avoid.
// A join can only ever ADD a badge; it cannot remove a row.
//
// The merge is pure and lives here, apart from the hook, so it can be tested
// without the Supabase client (TCSD-0049).
// ============================================================================

/** Anything carrying an optional facility id — every route row shape does. */
export interface HasFacilityId {
  facility_id: string | null;
}

/**
 * Split rows into the ones a route should DRIVE to and the ones it should not.
 *
 * Ben's decision, 2026-08-21: a paused facility does not appear on Today or
 * This Week. He wanted that from the start and had accepted a badge only
 * because an earlier session told him hiding it would be near-impossible to
 * undo. It is not: the rotation record (`am_route_assignments`) is never
 * touched, so the list simply stops matching it and resume makes it match
 * again with nothing to restore.
 *
 * Returns BOTH halves rather than just the survivors, because the hidden count
 * has to be shown. His standing rule is that nothing may disappear from a
 * screen; a filtered list that says "1 paused — not shown" honours both the
 * rule and the preference, while a silently shorter list honours neither.
 */
export function partitionByService<T extends HasFacilityId & { service_state?: string | null }>(
  rows: readonly T[],
): { serviced: T[]; paused: T[] } {
  const serviced: T[] = [];
  const paused: T[] = [];
  for (const r of rows) {
    // A row with no facility cannot be paused, whatever its `service_state`
    // says. Office stops, "AM Meeting" and other label-only stops have no
    // facility behind them, and hiding one because a stray field came through
    // set would drop a real commitment off an AM's day. `withServiceState`
    // already nulls those, but this function must not depend on its caller
    // having done that.
    const hide = !!r.facility_id && isPausedState(r.service_state);
    (hide ? paused : serviced).push(r);
  }
  return { serviced, paused };
}

/**
 * Attach `service_state` to each row from a facility_id → state map.
 *
 * Three things this gets right that a one-line `.map()` at each call site kept
 * getting wrong:
 *
 *   1. **A miss is `null`, never `undefined`.** `Record` lookups return
 *      `undefined` for an absent key, and `undefined` reaching a badge
 *      component is how a chip renders the word "undefined". Every path here
 *      coalesces.
 *   2. **A row with no facility_id gets `null`, not a lookup on `"null"`.**
 *      Office stops and label-only stops have no facility.
 *   3. **The row is spread, not rebuilt**, so a caller that has already
 *      enriched a row (the week view's `display_contact`) does not lose it.
 *
 * Rows are returned in input order; nothing is filtered. A paused facility
 * must never disappear.
 */
export function withServiceState<T extends HasFacilityId>(
  rows: readonly T[],
  byFacility: Readonly<Record<string, string | null>>,
): Array<T & { service_state: string | null }> {
  return rows.map((r) => ({
    ...r,
    service_state: r.facility_id ? byFacility[r.facility_id] ?? null : null,
  }));
}

/**
 * The service state to render for a facility.
 *
 * NULL means "never touched by the pause RPCs", which is Active — the state of
 * almost every row. Callers must not compare to 'Active' directly; that test
 * reads as false for the majority case and is how a chip ends up on 200
 * facilities that are being served normally.
 */
export function serviceStateOf(
  byFacility: Readonly<Record<string, string | null>>,
  facilityId: string | null | undefined,
): string {
  if (!facilityId) return 'Active';
  return byFacility[facilityId] ?? 'Active';
}

/** True only for a facility whose service is paused. */
export function isPausedState(state: string | null | undefined): boolean {
  return (state ?? 'Active') === 'Suspended';
}

/**
 * How many paused FACILITIES these stops cover.
 *
 * Counted by facility, not by row: a day can legitimately carry two stops at
 * the same site (a materialised day stop plus an ad-hoc one), and the week
 * view lists a facility once per scheduled day. The header says "1 paused" and
 * the AM counts badges — if those two numbers disagree the header is noise.
 *
 * Takes the two fields rather than a row type, because the shapes differ:
 * `ResolvedStop` uses camelCase (`facilityId` / `serviceState`) and the view
 * rows use the database's snake_case. One signature, callers adapt.
 */
export function countPausedFacilities(
  stops: ReadonlyArray<{
    facilityId: string | null;
    serviceState: string | null | undefined;
  }>,
): number {
  const seen = new Set<string>();
  for (const s of stops) {
    if (s.facilityId && isPausedState(s.serviceState)) seen.add(s.facilityId);
  }
  return seen.size;
}
